import { Box, Collapse, Dialog, IconButton, Typography } from '@mui/material'
import { XCloseIcon } from '@untitled-theme/icons-react'
import { useState } from 'react'

import { getRetrofitDisplayName } from '@/features/measures-overview/components/MeasuresTable/MeasuresTable.types'
import { BedarfVerbrauchLabel, BedarfVerbrauchType } from '@/shared/components/BedarfVerbrauchLabel'
import Button from '@/shared/components/Button'

import AmortizationTab from './AmortizationTab'
import DetailTabToggle from './DetailTabToggle'
import { DetailTab } from './DetailTabToggle.types'
import type { MeasureDetailsPopupProps } from './MeasureDetailsPopup.types'
import OverviewTab from './OverviewTab'

const MeasureDetailsPopup = ({
  open,
  measureC,
  measureD,
  assessment,
  financing,
  onClose,
  onAddToSelection,
}: MeasureDetailsPopupProps) => {
  const [selectedTab, setSelectedTab] = useState<DetailTab>(DetailTab.Overview)

  if (!measureC || !measureD) return null

  const displayName = getRetrofitDisplayName(measureC.retrofitName)

  const handleAddToSelection = () => {
    onAddToSelection!(measureC.retrofitName)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #E1E1E4',
            boxShadow: 'none',
            margin: '24px',
            maxWidth: '700px',
          },
        },
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
    >
      <Box
        sx={{
          paddingX: '16px',
          paddingY: '24px',
        }}
      >
        {/* Header with close button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 3,
            gap: '24px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '32px',
            }}
          >
            {displayName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <BedarfVerbrauchLabel type={BedarfVerbrauchType.VERBRAUCH} />
            <IconButton
              onClick={onClose}
              sx={{
                color: '#74777F',
                padding: 0,
                '&:hover': {
                  backgroundColor: '#F5F5F7',
                },
              }}
            >
              <XCloseIcon style={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Tab Selector */}
        <Box sx={{ marginTop: '24px', marginBottom: 3 }}>
          <DetailTabToggle selectedTab={selectedTab} onTabChange={setSelectedTab} />
        </Box>

        {/* Tab Content */}
        <Collapse in={selectedTab === DetailTab.Overview} timeout={300} unmountOnExit>
          <OverviewTab measureC={measureC} measureD={measureD} assessment={assessment} />
        </Collapse>

        <Collapse in={selectedTab === DetailTab.Amortization} timeout={300} unmountOnExit>
          <AmortizationTab
            retrofitEconomics={measureC.economics}
            retrofitName={displayName}
            financing={financing}
          />
        </Collapse>

        {/* Shared Button */}
        {onAddToSelection && (
          <Box sx={{ marginTop: 3 }}>
            <Button
              fullWidth
              onClick={handleAddToSelection}
              height="50px"
              radius="16px"
              sx={{ fontSize: '14px' }}
            >
              Jetzt in die Auswahl aufnehmen
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

export default MeasureDetailsPopup
